import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import {
  ISnListQueryParameters,
  ISnListResponse,
  ISnMapQueryParams,
  ISnMapResponse,
  transformListResponse,
} from '@lib/api';
import { FsPanorama } from '@lib/api/entities';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class PanoramaAPI {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public getPanoramasList(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<FsPanorama>> {
    return this._httpClient.get('/api/maps/panoramas/', { params }).pipe(
      map((response: any) =>
        transformListResponse(response, (item) => new FsPanorama(item), {
          ordering: response.ordering,
          clusterResults: response.clusterResults || [],
        }),
      ),
    );
  }

  public getMapPanoramas(
    params?: ISnListQueryParameters | ISnMapQueryParams,
  ): Observable<ISnMapResponse<FsPanorama>> {
    return this._httpClient.get('/api/maps/panoramas/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        ordering: response.ordering,
        results: response.results.map((panorama) => new FsPanorama(panorama)),
        clusterResults: response.clusterResults || [],
      })),
    );
  }

  public createPanorama(data: any): Observable<FsPanorama> {
    return this._httpClient.post('/api/maps/panoramas/', data).pipe(
      map((response: any) => new FsPanorama(response)),
      tap({
        next: () => this._toastrService.success(t('panoramas.messages.created')),
        error: () => this._toastrService.error(t('panoramas.errors.createFailed')),
      }),
    );
  }

  public updatePanorama(id: string, data: any): Observable<FsPanorama> {
    return this._httpClient.patch(`/api/maps/panoramas/${id}/`, data).pipe(
      map((panorama) => new FsPanorama(panorama)),
      tap({
        next: () => this._toastrService.success(t('panoramas.messages.updated')),
        error: () => this._toastrService.error(t('panoramas.errors.updateFailed')),
      }),
    );
  }

  public deletePanorama(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('panoramas.dialogs.deletePanorama'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/maps/panoramas/${id}/`)),
        tap({
          next: () => this._toastrService.success(t('panoramas.messages.deleted')),
          error: () => this._toastrService.error(t('panoramas.errors.deleteFailed')),
        }),
      );
  }
}
